import React, {ChangeEvent, useCallback, useEffect, useMemo, useState} from "react";
import {Accordion} from "radix-ui";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faChevronRight} from "@fortawesome/pro-solid-svg-icons";
import {HighlighterTool} from "@treegram/tools-highlighter";

import useViewerItemsVisibilityContext from "@/services/context/viewer/items-visibility/use-viewer-items-visibility-context.ts";
import useProjectMeasureContext from "@/pages/takeoff/context/project-measure/use-project-measure-context.ts";
import useViewerContext from "@/services/context/viewer/instance/use-viewer-context.ts";

import Card from "@/components/card";
import CheckBox from "@/components/checkbox";
import MeasureType from "@/pages/takeoff/context/project-measure/models/measure-type.ts";
import ToggleButton from "@/components/buttons/button-toggle";

const MeasureOverviewTree: React.FC = () => {
    
    const {getTool} = useViewerContext();
    const {projectMeasureTypes, projectMeasures} = useProjectMeasureContext();
    const {visibleItems, addItems, removeItems} = useViewerItemsVisibilityContext();

    const measures = useMemo(() => projectMeasures.buildings.flatMap(b => b.storeys).flatMap(s => s.measures), [projectMeasures]);
    const displayedMeasureTypes = useMemo(() => {
        const usedTypeIds = new Set(measures.map(m => m.measureTypeId));
        return projectMeasureTypes.filter(mt => usedTypeIds.has(mt.measureTypeId));
    }, [projectMeasureTypes, measures]);

    const [highlightedMeasureTypeId, setHighlightedMeasureTypeId] = useState<string | undefined>(undefined);

    const [measureTypes, setMeasureTypes] = useState<Record<string, MeasureType[]>>({});
    const checkedMeasureTypes = useMemo(() => {
        const visibleIds = new Set(visibleItems);

        return displayedMeasureTypes.filter(mt => {
            const geometryIds = measures
                .filter(m => m.measureTypeId === mt.measureTypeId)
                .flatMap(m => m.geometryRepresentations.map(g => g.geometryId));

            return geometryIds.length > 0 && geometryIds.every(id => visibleIds.has(id));
        }).map(mt => mt.measureTypeId);
    }, [displayedMeasureTypes, measures, visibleItems]);

    const handleHighLightMeasureType = useCallback((measureTypeId: string) => {
        setHighlightedMeasureTypeId(measureTypeId);

        const filtered = measures.filter(measure => measure.measureTypeId === measureTypeId);
        const geometriesIds = filtered.flatMap(measure => measure.geometryRepresentations.map(geometry => geometry.geometryId));

        getTool(HighlighterTool)?.highlightItems(geometriesIds);
    }, [measures, getTool]);

    const handleMeasureVisibilityChange = useCallback((measureTypeId: string, visible: boolean) => {
        const measuresOfThisType = measures
            .filter(m => m.measureTypeId === measureTypeId)
            .map(m => m.measureId).filter((m): m is Extract<typeof m, string> => m != null);

        const filtered = measures.filter(m => m.measureId != null && measuresOfThisType.includes(m.measureId));
        const geometriesIds = filtered.flatMap(m => m.geometryRepresentations.map(gm => gm.geometryId));

        if (visible) addItems(geometriesIds);
        else removeItems(geometriesIds);

    }, [measures, addItems, removeItems]);

    const handleMeasureTypeVisibilityCheckChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        const {measureTypeId} = e.currentTarget.dataset;

        if (measureTypeId == null) return;

        handleMeasureVisibilityChange(measureTypeId, e.target.checked);
    }, [handleMeasureVisibilityChange]);

    const isAllMeasureTypesChecked = displayedMeasureTypes.every(mt =>
        checkedMeasureTypes.includes(mt.measureTypeId)
    );

    useEffect(() => {
        const typePerKeyWord = displayedMeasureTypes.reduce((acc, mt) => {
            const word = mt.name.split(' ')[0];
            acc[word] = acc[word] ?? [];
            acc[word].push(mt);
            return acc;
        }, {} as Record<string, MeasureType[]>);

        const sortedMeasureTypes = Object.keys(typePerKeyWord)
            .sort((a, b) => a.localeCompare(b, 'fr', {sensitivity: 'base'}))
            .reduce<Record<string, MeasureType[]>>((acc, key) => {
                acc[key] = typePerKeyWord[key].sort((a, b) =>
                    a.name.localeCompare(b.name, 'fr', {sensitivity: 'base'})
                );

                return acc;
            }, {});

        setMeasureTypes(sortedMeasureTypes);
    }, [displayedMeasureTypes]);

    const handleAllMeasureTypesVisibilityCheckChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        displayedMeasureTypes.forEach(mt => {
            handleMeasureVisibilityChange(mt.measureTypeId, e.target.checked);
        });
    }, [displayedMeasureTypes, handleMeasureVisibilityChange]);
    
    if(measures.length === 0)
        return null;

    return (
        <Card additionalClasses='overflow-hidden h-fit py-0.5' additionalContentClasses='flex flex-col min-h-0'>
            <div className='flex flex-col gap-y-0.5 overflow-y-auto pl-2 py-2 pr-4 text-sm'>
                <div className='flex flex-row items-center gap-x-2 ml-4.5'>
                    <CheckBox checked={isAllMeasureTypesChecked}
                              onChange={handleAllMeasureTypesVisibilityCheckChange}/>
                    <span>Tout afficher</span>
                </div>

                <Accordion.Root type='multiple'>
                    {Object.entries(measureTypes).map(([word, measuresOfThisWord]) => {

                        const allMeasuresOfThisWord = measures.filter(m => measuresOfThisWord.some(mw => mw.measureTypeId === m.measureTypeId));
                        if (allMeasuresOfThisWord.length === 0) {
                            return null;
                        }

                        return (
                            <Accordion.Item value={word} key={word}>
                                <Accordion.Header>
                                    <div className='flex flex-row items-center gap-x-1'>
                                        <Accordion.Trigger className='group'>
                                            <FontAwesomeIcon icon={faChevronRight}
                                                             className='transition-transform duration-300 ease-in-out group-data-[state=open]:rotate-90 text-xs'/>
                                        </Accordion.Trigger>
                                        <CheckBox checked={measuresOfThisWord.every(mt => checkedMeasureTypes.find(ct => ct === mt.measureTypeId))}
                                            onChange={(e) => {
                                                measuresOfThisWord.forEach(mt => {
                                                    handleMeasureVisibilityChange(mt.measureTypeId, e.target.checked);
                                                })
                                            }}/>

                                        <Accordion.Trigger>
                                            <span className='select-none'>{word}</span>
                                        </Accordion.Trigger>

                                        <span className='ml-auto text-xs text-gray-600'>
                                            ( x {measuresOfThisWord.flatMap(mt => measures.filter(m => m.measureTypeId === mt.measureTypeId).length).reduce((a, b) => a + b)} )
                                        </span>
                                    </div>
                                </Accordion.Header>

                                <Accordion.Content
                                    className='overflow-hidden data-[state=open]:animate-slideDown data-[state=closed]:animate-slideUp'>
                                    <div className='flex flex-col gap-y-1 ml-8 my-0.75'>
                                        {measuresOfThisWord.map(mt => {
                                            const measuresOfThisType = measures.filter(m => m.measureTypeId === mt.measureTypeId).sort((a, b) => a.name.localeCompare(b.name, 'fr', {sensitivity: 'base'}));
                                            const quantity = measuresOfThisType.length;
                                            if (quantity === 0) {
                                                return null;
                                            }
                                            return (
                                                <div
                                                    className='grid grid-cols-[auto_1fr_auto] flex-1 gap-x-1.5 items-center'
                                                    key={mt.measureTypeId}>
                                                    <CheckBox checked={checkedMeasureTypes.includes(mt.measureTypeId)}
                                                              onChange={handleMeasureTypeVisibilityCheckChange}
                                                              data-measure-type-id={mt.measureTypeId}/>

                                                    <ToggleButton id={`div-measure-type-${mt.measureTypeId}`}
                                                                  variant='ghost' size='2xs'
                                                                  setIsToggled={() => handleHighLightMeasureType(mt.measureTypeId)}
                                                                  toggled={highlightedMeasureTypeId === mt.measureTypeId}
                                                                  redoToggleAction={true}>
                                                        <div className='flex flex-row flex-1 gap-x-1.5'>
                                                            <span className='text-gray-900'>{mt.name}</span>
                                                            <span className='ml-auto text-xs text-gray-600'>( x {quantity} )</span>
                                                        </div>
                                                    </ToggleButton>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </Accordion.Content>
                            </Accordion.Item>
                        );
                    })}
                </Accordion.Root>
            </div>

        </Card>
    )
}

export default MeasureOverviewTree;
